// frontend/src/app/app.ts
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnInit,
  computed,
  effect,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RouterOutlet, Router, NavigationEnd } from '@angular/router';
import { resolveActiveRouteViewMode } from '../utils/route-view-mode.utils';
import { CommonModule } from '@angular/common';
import { TranslocoModule } from '@jsverse/transloco';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Sidebar, AppLoaderComponent, AppBootErrorComponent } from '../components';
import { filter } from 'rxjs/operators';
import { AuthService } from '../services/auth/auth';
import { StateStoreService } from '../services/state-store/state-store';
import { ThemeService } from '../services/theme/theme.service';
import { RecoveryFlowService } from '../services/recovery-flow/recovery-flow.service';
import { ProfileCompletionFlowService } from '../services/profile-completion-flow/profile-completion-flow.service';
import { BuildVersionCheckService } from '../services/build-version-check/build-version-check';
import { BootHealthService } from '../services/boot-health/boot-health.service';
import { ShellChromeService } from '../services/shell-chrome/shell-chrome.service';
import { appLogoPublicFilenameForTheme } from '../constants/app-branding.constants';
import { ActionGlyphComponent } from '../components/action-glyph/action-glyph';
import {
  PAYMENT_METHOD_RETURN_PARAM,
  cardSetupReturnParams,
} from '../utils/billing-readiness.utils';

@Component({
  selector: 'app-root',
  imports: [
    CommonModule,
    RouterOutlet,
    Sidebar,
    ActionGlyphComponent,
    AppLoaderComponent,
    AppBootErrorComponent,
    TranslocoModule,
    MatTooltipModule,
  ],
  templateUrl: './app.html',
  styleUrl: './app.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class App implements OnInit {
  /** Routes that render their own full-screen layout (no shell sidebar).
   *  These surfaces own every pixel — wrapping them in the app shell
   *  would double-frame the canvas.
   *
   * `/login` doesn't need to be listed because the app-shell branch only
   * fires when the user is authenticated; an unauthenticated user on
   * `/login` falls through to the bare `<router-outlet>` below it.
   */
  private static readonly FULL_SCREEN_ROUTES = [
    '/edit-document',
    '/edit-assessment',
    '/complete-profile',
    '/import',
    // The zero-grant screen is the one entry here for the opposite reason to
    // the others: it owns every pixel because there is nothing to frame. A
    // user with no workspace has no customer to pick and no section to be in,
    // so the sidebar would render manager headings over surfaces they cannot
    // reach. It carries a sign-out button instead of an identity bar.
    '/no-workspace',
  ];

  private static isFullScreenRoute(url: string): boolean {
    return App.FULL_SCREEN_ROUTES.some(route => url.startsWith(route));
  }

  private readonly destroyRef = inject(DestroyRef);
  public readonly authService = inject(AuthService);
  public readonly stateService = inject(StateStoreService);
  protected readonly themeService = inject(ThemeService);
  private readonly router = inject(Router);
  /** Set by `RecoverMfa` for the entire lifetime of the recovery flow.
   * The template gates the bare-outlet branch on this so the recovery
   * component instance survives Cognito's mid-flow `isAuthenticated`
   * flip (see `RecoveryFlowService` and the long comment there). */
  protected readonly recoveryFlow = inject(RecoveryFlowService);
  private readonly profileFlow = inject(ProfileCompletionFlowService);

  /**
   * Boot-readiness + escape hatch. Owns `isBooting`, `navDone`,
   * `phase`, `stuckSignals`. The shell reads these directly rather
   * than composing them itself. See `boot-health.service.ts`.
   */
  protected readonly bootHealth = inject(BootHealthService);
  protected readonly shellChrome = inject(ShellChromeService);
  /** Mirror of shell sidenav open state — drives the far-left toggle. */
  protected readonly isSidebarOpen = this.shellChrome.sidebarOpen;

  protected toggleSidebar(): void {
    this.shellChrome.toggleSidebar();
  }

  /** `true` once a newer deploy has been detected on the server. The
   * shell surfaces this as a "new version available" banner with a
   * Reload button — we never auto-reload, since that would discard
   * any in-progress work the user has on screen. The polling itself
   * is started from `provideAppInitializer` in `app.config.ts`. */
  protected readonly buildStale = inject(BuildVersionCheckService).stale;

  protected readonly title = signal('compliance-platform-fe');
  /** Raw "is this URL allowed to show the shell sidebar?" — combined with
   *  auth state and recovery flag in {@link showSidebar}. Full-screen
   *  routes render their own layout, so the app-shell sidebar stays off. */
  private readonly urlAllowsShell = signal(!App.isFullScreenRoute(window.location.pathname));
  protected readonly showAppContainer = computed(
    () => this.authService.isAuthenticated() && !this.authService.isLoading()
  );
  protected readonly isStateReady = computed(() => this.stateService.isStateReady());

  /** True when the entire bootstrap sequence is complete and the app shell should render. */
  protected readonly isAppReady = computed(
    () => this.showAppContainer() && this.isStateReady() && this.bootHealth.navDone()
  );

  /** Sidebar shows when:
   *   - the URL allows shell chrome (not a full-screen route), AND
   *   - the app is fully booted with an authenticated user, AND
   *   - the recovery flow is NOT active. */
  protected readonly showSidebar = computed(
    () => this.urlAllowsShell() && this.isAppReady() && !this.recoveryFlow.active()
  );

  /** Overlay loader during boot.
   *
   *  Hidden during the MFA recovery flow (recovery owns the screen).
   *  Otherwise mirrors `BootHealthService.isBooting()` — that service
   *  composes auth / state / nav / profile-completion readiness and
   *  also drives the two-stage deadline that swaps the spinner for
   *  `<app-boot-error>` when boot is wedged. */
  protected readonly showBootLoader = computed(
    () => !this.recoveryFlow.active() && this.bootHealth.isBooting()
  );

  /** True once boot has been stuck long enough to surface the escape
   *  hatch UI in place of the spinner. */
  protected readonly showBootError = computed(
    () => this.showBootLoader() && this.bootHealth.phase() === 'stalled'
  );

  /** True when boot has been slow but not yet stalled — drives the
   *  "Still loading…" hint under the spinner. */
  protected readonly showSlowHint = computed(() => this.bootHealth.phase() === 'slow');

  protected readonly logoSrc = computed(() =>
    appLogoPublicFilenameForTheme(this.themeService.theme())
  );
  protected readonly showUpdateNotice = signal(
    localStorage.getItem('payment_migration_v1') !== 'true'
  );

  constructor() {
    // Billing details are mandatory, but `profileCompletionGuard` is a
    // `CanActivate` — it only runs *on navigation*. Readiness is fetched
    // asynchronously when the org is selected, so for an existing account
    // the answer lands after the initial route has already activated: the
    // guard let the user through on a fail-open read, and nothing
    // re-evaluates it until they happen to click something. They sit on
    // the page they landed on, and get bounced from a later click for
    // reasons that have nothing to do with where they were going.
    //
    // This applies the same verdict the moment the answer arrives.
    // Deliberately the same computed the guards read — a second opinion
    // here would let this and the guards disagree, and disagreement
    // between something that redirects IN and something that redirects
    // OUT is a navigation loop.
    effect(() => {
      // `isAppReady` keeps this off the boot path, where state is still
      // settling and the guards are already running on every hop.
      if (!this.isAppReady() || this.recoveryFlow.active()) return;
      if (!this.stateService.requiresBillingDetails()) return;

      // `router.url` is read untracked on purpose: the trigger is the
      // readiness answer changing, not navigation. Navigation is already
      // covered by the guards.
      const url = this.router.url;
      if (url.startsWith('/complete-profile')) return;

      this.router.navigate(['/complete-profile'], {
        queryParams: {
          returnUrl: url,
          // Same forwarding as `profileCompletionGuard` — whichever of
          // the two fires on the return from Stripe, the wizard has to
          // learn that a card was just entered.
          ...cardSetupReturnParams(
            this.router.parseUrl(url).queryParams[PAYMENT_METHOD_RETURN_PARAM]
          ),
        },
      });
    });
  }

  ngOnInit(): void {
    // Start the boot-health deadline timers AND subscribe to Router
    // events for `navDone`. Idempotent — see service docs.
    this.bootHealth.start();

    // Handle case where initial navigation completed before ngOnInit
    // — layout visibility still needs syncing here (BootHealthService
    // owns nav-readiness but not the component's layout flags).
    if (this.router.navigated) {
      this.updateLayoutVisibility(this.router.url);
    }

    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((event: NavigationEnd) => {
        this.updateLayoutVisibility(event.url);
        // Self-heal the billing gate. Readiness is read once per org
        // selection; if that attempt fails there is nothing left to
        // re-trigger it, and the gate stays open for the session. This
        // is a no-op once an answer is in, and capped inside the store
        // so a real outage doesn't fire on every navigation.
        this.stateService.ensurePaymentMethodStatus();
      });
  }

  protected reloadForUpdate(): void {
    window.location.reload();
  }

  /**
   * Boot-error UI handlers. The component itself handles "Reset &
   * reload" internally (calls `resetAppState()` and then
   * `__reloadWithFreshShell()`); these two handle the lighter
   * actions: "Reload" → cache-busting reload, "Keep waiting" →
   * dismiss the panel and return to the spinner.
   */
  protected onBootErrorReload(): void {
    const reload = (window as unknown as { __reloadWithFreshShell?: () => void })
      .__reloadWithFreshShell;
    if (typeof reload === 'function') {
      reload();
    } else {
      window.location.reload();
    }
  }

  protected onBootErrorKeepWaiting(): void {
    this.bootHealth.dismissStalled();
  }

  private updateLayoutVisibility(url: string): void {
    this.urlAllowsShell.set(!App.isFullScreenRoute(url));
    this.syncViewModeFromRoute();
  }

  /** Sync the shell's persona from the freshly-activated route.
   *
   *  `'preserve'`-tagged routes (e.g. `/tasks`, `/import`) are bimodal —
   *  the persona the user was on when they navigated in is what they
   *  should see when they leave. Untagged routes default to customer.
   *
   *  Route → persona is one leg; the reverse (persona swap → route
   *  redirect) lives in `StateStoreService.enterManagingCustomer` /
   *  `enterManagerView`, which navigate to that persona's home so a
   *  swap can't strand the user on a page whose sidebar/route set
   *  doesn't fit the new persona.
   *
   *  URL precedence: if the incoming URL explicitly carries `?view=`, we
   *  DO NOT override — that's the whole point of URL-as-source-of-truth
   *  (share-link, refresh, deep-link). The state-store's sticky-param
   *  reconciler will have already applied the URL's persona by the time
   *  this runs. Route defaults only apply when the URL is silent. */
  private syncViewModeFromRoute(): void {
    const mode = resolveActiveRouteViewMode(this.router);
    if (mode === 'preserve') return;
    const urlHasExplicitView =
      this.router.parseUrl(this.router.url).queryParams['view'] !== undefined;
    if (urlHasExplicitView) return;
    this.stateService.setViewMode(mode === 'manager' ? 'manager' : 'customer');
  }

  dismissUpdateNotice(): void {
    localStorage.setItem('payment_migration_v1', 'true');
    this.showUpdateNotice.set(false);
  }
}
